import React, { useState } from 'react'
import { DurationSelect } from './UploadResumeRevision'
import { Button } from '~/lib/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { Label } from '~/lib/ui/label'
import { useOrientationCall } from './OrientationCallDetail'
import PrivacySettings from './PrivacySettings'
import { Textarea } from '~/lib/ui/textarea'
import { CheckCircleIcon } from 'lucide-react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import Dropzone from './Dropzone'
import { useMutation } from '@tanstack/react-query'
import { queryClient } from './CandidateProvider'
import { apiPatch } from '~/util/api'
import { useToast } from '~/lib/ui/toast'

const OrientationFeedbackDialog = ({ children }) => {
  const currentOrientationCall = useOrientationCall()
  const [open, setOpen] = useState(false)
  const [duration, setDuration] = useState('')
  const [privacySettings, setPrivacySettings] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const allPrivacySettings = ['csm', 'coach', 'admin']
  const formPrivacySettings =
    privacySettings.indexOf('all') > -1 ? allPrivacySettings : privacySettings
  const [noteDetails, setNoteDetails] = useState('')
  const [noteToCandidate, setNoteToCandidate] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const toast = useToast()
  const { candidateId } = currentOrientationCall || {}

  const runMutation = async (formData) => {
    setIsSaving(true)
    const { id } = currentOrientationCall
    const patchPath = `/coach/orientation_calls/${id}.json`
    const response = await apiPatch(patchPath, formData, true)
    return response
  }

  const updateOrientationCallMutation = useMutation({
    mutationFn: runMutation,
    onSuccess: () => {
      setIsSaving(false)
      setOpen(false)
      queryClient.invalidateQueries(['serviceRequests', candidateId])
      toast({
        title: 'Success',
        description: 'Orientation Call Feedback Saved',
      })
    },
    onError: (error) => {
      setIsSaving(false)
      console.error('Error saving orientation call feedback:', error)
      toast({
        title: 'Error',
        description: 'Failed to save orientation call feedback',
        variant: 'destructive',
      })
    },
  })

  const saveClick = async () => {
    if (isSaving) return

    const f = new FormData()
    f.append('orientation_call[coach_note_attributes][duration]', duration)
    formPrivacySettings.forEach((setting) => {
      f.append(
        'orientation_call[coach_note_attributes][privacy_settings][]',
        setting,
      )
    })
    f.append('orientation_call[coach_note_attributes][note]', noteDetails)
    f.append('orientation_call[coach_message]', noteToCandidate)
    selectedFiles.forEach((file) => {
      f.append('orientation_call[coach_documents][]', file)
    })

    updateOrientationCallMutation.mutate(f)
  }

  const removeFile = (index) => (event) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, fileIndex) => fileIndex !== index),
    )
    event.stopPropagation()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={() => setOpen(!open)}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          variant="link"
          className="justify-start font-normal text-intoo-blue-medium hover:text-link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2 className="text-2xl">Orientation Call Feedback</h2>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[80vh] overflow-y-auto px-4 pb-4">
          <DialogDescription className="p-0 text-lg">
            Add feedback for the candidate after the orientation call.
          </DialogDescription>
          <div className="py-4">
            <Label htmlFor="duration" className="text-lg !font-medium">
              Duration (Minutes)
            </Label>
            <DurationSelect onValueChange={setDuration} />
          </div>
          <div className="pb-4">
            <Label htmlFor="noteDetails" className="text-lg !font-medium">
              Internal System Admin Notes
            </Label>
            <PrivacySettings
              setPrivacySettings={setPrivacySettings}
              privacySettings={privacySettings}
            />
            <Textarea
              className="min-h-56 w-full"
              name="noteDetails"
              value={noteDetails}
              onChange={(e) => setNoteDetails(e.target.value)}
            />
          </div>
          <hr className="border-t-2 border-gray-300" />
          <div className="py-4">
            <Label htmlFor="noteToCandidate" className="text-lg !font-medium">
              Please record any remarks with this update. Comments will be
              viewed by the candidate.
            </Label>
            <Textarea
              className="min-h-56 w-full"
              name="noteToCandidate"
              value={noteToCandidate}
              onChange={(e) => setNoteToCandidate(e.target.value)}
            />
          </div>
          <div className="py-4">
            <Dropzone
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
            >
              {selectedFiles.length > 0 ? (
                <SelectedFilesDisplay
                  selectedFiles={selectedFiles}
                  removeFile={removeFile}
                />
              ) : (
                <p className="text-lg">
                  Drag and drop files here, or click to select files.
                </p>
              )}
            </Dropzone>
          </div>
          <div className="flex flex-row justify-end">
            <Button className="inline" onClick={saveClick} disabled={isSaving}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default OrientationFeedbackDialog

const SelectedFilesDisplay = ({ selectedFiles, removeFile }) => {
  return selectedFiles.map((file, index) => (
    <p
      className="mb-4 flex flex-row items-center"
      key={`${file.name}-${index}`}
    >
      <CheckCircleIcon className="mr-2 inline w-6 text-intoo-green-coachcta" />
      {`${file.name} (${(file.size / 1024).toFixed(2)} KB) ${file.type ? ` (${file.type})` : ''}`}
      <Button
        variant="link"
        className="ml-2 text-red-900"
        title="Remove this file"
        onClick={removeFile(index)}
      >
        <XMarkIcon className="inline w-5 stroke-2" />
      </Button>
    </p>
  ))
}
